import type { ProductGroupSegment } from './report-product-groups.model';
import { prepareProductGroupsDisplaySegments } from './report-product-groups.helpers';
import {
  PRODUCT_GROUPS_DONUT_CENTER_X_PERCENT,
  PRODUCT_GROUPS_DONUT_CENTER_Y_PERCENT,
  PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT,
  PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT,
} from './report-product-groups.constants';

/** Donut geometry in SVG viewBox units (0–100). */
export const PRODUCT_GROUPS_DONUT_INNER_RADIUS_VB = (24 / 46) * 50;
export const PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB = 50;
/**
 * Path degrees (before the chart's −90° SVG rotation) for visual 3 o'clock.
 */
export const PRODUCT_GROUPS_DONUT_THREE_O_CLOCK_PATH_DEG = 90;
/**
 * Angular gap between slices (degrees). ~10× the prior 0.3vb white-stroke separator
 * at the outer ring ({@link PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB}).
 */
export const PRODUCT_GROUPS_DONUT_SLICE_GAP_DEG =
  (0.3 / PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB) * (180 / Math.PI) * 10;

export type ProductGroupDonutSliceGeometry = ProductGroupSegment & {
  startAngle: number;
  endAngle: number;
  midAngle: number;
  proposalOuterRadiusVb: number;
  baselineOuterRadiusVb: number;
  /** Outermost visible edge at {@link midAngle} — used for leader-line anchors. */
  leaderOuterRadiusVb: number;
};

/**
 * Outer radius for one slice — baseline sits on the reference ring; proposal
 * above/below baseline extends outward/inward (same ratio model as SBEF bar height).
 */
export function getProductGroupSliceOuterRadius(
  baselineCo2ePerGfa: number,
  proposalCo2ePerGfa: number,
  innerRadius = PRODUCT_GROUPS_DONUT_INNER_RADIUS_VB,
  referenceOuterRadius = PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB,
): number {
  if (baselineCo2ePerGfa <= 0) return innerRadius;
  const ratio = proposalCo2ePerGfa / baselineCo2ePerGfa;
  return innerRadius + ratio * (referenceOuterRadius - innerRadius);
}

function polarToCartesian(
  cx: number,
  cy: number,
  angle: number,
  radius: number,
): { x: number; y: number } {
  const rad = (angle * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
}

function degToRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Target corner radius (vb) — half the arc length of
 * {@link PRODUCT_GROUPS_DONUT_SLICE_GAP_DEG} at the constant reference outer
 * radius, so every slice gets the same corner radius regardless of how far
 * its own outer edge extends.
 */
const PRODUCT_GROUPS_DONUT_TARGET_CORNER_RADIUS_VB =
  (PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB *
    degToRad(PRODUCT_GROUPS_DONUT_SLICE_GAP_DEG)) /
  2;

/** Corner radius (vb) — constant, clamped to what this slice can geometrically fit. */
export function getProductGroupDonutSliceCornerRadiusVb(
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number,
): number {
  const sweepRad = degToRad(endAngle - startAngle);
  if (sweepRad <= 0) return 0;

  const maxByOuterArc = (outerRadius * sweepRad) / 2;
  const maxByInnerArc = (innerRadius * sweepRad) / 2;
  const maxByThickness = (outerRadius - innerRadius) / 2;

  return Math.min(
    PRODUCT_GROUPS_DONUT_TARGET_CORNER_RADIUS_VB,
    maxByOuterArc,
    maxByInnerArc,
    maxByThickness,
  );
}

function buildSharpVariableOuterDonutPath(
  startAngle: number,
  endAngle: number,
  segmentOuterRadius: number,
  cx: number,
  cy: number,
  innerRadius: number,
): string {
  const outerStart = polarToCartesian(cx, cy, startAngle, segmentOuterRadius);
  const outerEnd = polarToCartesian(cx, cy, endAngle, segmentOuterRadius);
  const innerStart = polarToCartesian(cx, cy, startAngle, innerRadius);
  const innerEnd = polarToCartesian(cx, cy, endAngle, innerRadius);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${segmentOuterRadius} ${segmentOuterRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

/** Donut slice path with a per-segment outer radius and rounded corners. */
export function buildVariableOuterDonutPath(
  startAngle: number,
  endAngle: number,
  segmentOuterRadius: number,
  options?: {
    cx?: number;
    cy?: number;
    innerRadius?: number;
    cornerRadius?: number;
  },
): string {
  const cx = options?.cx ?? 50;
  const cy = options?.cy ?? 50;
  const innerRadius =
    options?.innerRadius ?? PRODUCT_GROUPS_DONUT_INNER_RADIUS_VB;
  const sweep = endAngle - startAngle;
  if (sweep <= 0) return '';

  const cornerRadius =
    options?.cornerRadius ??
    getProductGroupDonutSliceCornerRadiusVb(
      segmentOuterRadius,
      innerRadius,
      startAngle,
      endAngle,
    );

  if (cornerRadius <= 0) {
    return buildSharpVariableOuterDonutPath(
      startAngle,
      endAngle,
      segmentOuterRadius,
      cx,
      cy,
      innerRadius,
    );
  }

  const outerDelta = Math.min(
    (cornerRadius / segmentOuterRadius) * (180 / Math.PI),
    sweep / 2,
  );
  const innerDelta = Math.min(
    (cornerRadius / innerRadius) * (180 / Math.PI),
    sweep / 2,
  );
  const outerArcSweep = sweep - 2 * outerDelta;
  const innerArcSweep = sweep - 2 * innerDelta;
  const outerLargeArc = outerArcSweep > 180 ? 1 : 0;
  const innerLargeArc = innerArcSweep > 180 ? 1 : 0;
  const r = cornerRadius;

  const outerArcStart = polarToCartesian(
    cx,
    cy,
    startAngle + outerDelta,
    segmentOuterRadius,
  );
  const outerArcEnd = polarToCartesian(
    cx,
    cy,
    endAngle - outerDelta,
    segmentOuterRadius,
  );
  const radialOuterEnd = polarToCartesian(
    cx,
    cy,
    endAngle,
    segmentOuterRadius - r,
  );
  const radialInnerEnd = polarToCartesian(cx, cy, endAngle, innerRadius + r);
  const innerArcEnd = polarToCartesian(
    cx,
    cy,
    endAngle - innerDelta,
    innerRadius,
  );
  const innerArcStart = polarToCartesian(
    cx,
    cy,
    startAngle + innerDelta,
    innerRadius,
  );
  const radialInnerStart = polarToCartesian(
    cx,
    cy,
    startAngle,
    innerRadius + r,
  );
  const radialOuterStart = polarToCartesian(
    cx,
    cy,
    startAngle,
    segmentOuterRadius - r,
  );

  return [
    `M ${radialOuterStart.x} ${radialOuterStart.y}`,
    `A ${r} ${r} 0 0 1 ${outerArcStart.x} ${outerArcStart.y}`,
    `A ${segmentOuterRadius} ${segmentOuterRadius} 0 ${outerLargeArc} 1 ${outerArcEnd.x} ${outerArcEnd.y}`,
    `A ${r} ${r} 0 0 1 ${radialOuterEnd.x} ${radialOuterEnd.y}`,
    `L ${radialInnerEnd.x} ${radialInnerEnd.y}`,
    `A ${r} ${r} 0 0 1 ${innerArcEnd.x} ${innerArcEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${innerLargeArc} 0 ${innerArcStart.x} ${innerArcStart.y}`,
    `A ${r} ${r} 0 0 1 ${radialInnerStart.x} ${radialInnerStart.y}`,
    `L ${radialOuterStart.x} ${radialOuterStart.y}`,
    'Z',
  ].join(' ');
}

/**
 * Default layout start so the chart opens toward visual 3 o'clock:
 * even n — gap between slice n/2 and n/2+1; odd n — center of slice n/2.
 */
function getProductGroupDonutLayoutStartDeg(
  displaySegments: ProductGroupSegment[],
  baselineTotal: number,
  sweepableDeg: number,
  sliceGapDeg: number,
): number {
  const segmentCount = displaySegments.length;
  if (segmentCount === 0) return PRODUCT_GROUPS_DONUT_THREE_O_CLOCK_PATH_DEG;

  const sliceAngleDeg = (index: number) =>
    (displaySegments[index]!.value / baselineTotal) * sweepableDeg;

  const cumulativeStartDeg = (sliceIndex: number) => {
    let cumulative = 0;
    for (let i = 0; i < sliceIndex; i++) {
      cumulative += sliceAngleDeg(i) + sliceGapDeg;
    }
    return cumulative;
  };

  if (segmentCount % 2 === 0) {
    const leftSliceIndex = segmentCount / 2 - 1;
    const gapCenterDeg =
      cumulativeStartDeg(leftSliceIndex) +
      sliceAngleDeg(leftSliceIndex) +
      sliceGapDeg / 2;
    return PRODUCT_GROUPS_DONUT_THREE_O_CLOCK_PATH_DEG - gapCenterDeg;
  }

  const middleSliceIndex = Math.floor(segmentCount / 2);
  const middleMidDeg =
    cumulativeStartDeg(middleSliceIndex) + sliceAngleDeg(middleSliceIndex) / 2;
  return PRODUCT_GROUPS_DONUT_THREE_O_CLOCK_PATH_DEG - middleMidDeg;
}

export type BuildProductGroupDonutSliceGeometriesOptions = {
  /** Extra clockwise rotation applied to every slice (path degrees). */
  rotationOffsetDeg?: number;
};

/**
 * Slice geometries for the product-groups donut — shared by the chart and
 * leader-line layout so angles stay in sync.
 */
export function buildProductGroupDonutSliceGeometries(
  segments: ProductGroupSegment[],
  options?: BuildProductGroupDonutSliceGeometriesOptions,
): ProductGroupDonutSliceGeometry[] {
  const displaySegments = prepareProductGroupsDisplaySegments(segments);
  if (displaySegments.length === 0) return [];

  const baselineTotal = displaySegments.reduce(
    (sum, item) => sum + item.value,
    0,
  );
  const sliceGapDeg = PRODUCT_GROUPS_DONUT_SLICE_GAP_DEG;
  const sweepableDeg = 360 - displaySegments.length * sliceGapDeg;
  const rotationOffsetDeg = options?.rotationOffsetDeg ?? 0;
  let currentAngle =
    getProductGroupDonutLayoutStartDeg(
      displaySegments,
      baselineTotal,
      sweepableDeg,
      sliceGapDeg,
    ) + rotationOffsetDeg;

  const rawSlices = displaySegments.map((item) => {
    const angle = (item.value / baselineTotal) * sweepableDeg;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    const midAngle = (startAngle + endAngle) / 2;
    const proposalOuterRadiusVb = getProductGroupSliceOuterRadius(
      item.value,
      item.cloudValue,
    );
    currentAngle = endAngle + sliceGapDeg;

    return {
      ...item,
      startAngle,
      endAngle,
      midAngle,
      proposalOuterRadiusVb,
      baselineOuterRadiusVb: PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB,
      leaderOuterRadiusVb: Math.max(
        PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB,
        proposalOuterRadiusVb,
      ),
    };
  });

  // If any slice's outer radius exceeds baseline + headroom, scale all ring
  // thicknesses (outer − inner) down uniformly so the largest slice fits within
  // baseline × OVERFLOW_HEADROOM. Inner radius stays fixed; size relationships are preserved.
  const OVERFLOW_HEADROOM = 1.1;
  const maxAllowedOuter =
    PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB * OVERFLOW_HEADROOM;
  const maxProposalOuter = Math.max(
    0,
    ...rawSlices.map((s) => s.proposalOuterRadiusVb),
  );
  if (maxProposalOuter > maxAllowedOuter) {
    const innerR = PRODUCT_GROUPS_DONUT_INNER_RADIUS_VB;
    const scale = (maxAllowedOuter - innerR) / (maxProposalOuter - innerR);
    return rawSlices.map((s) => {
      const scaledProposalOuter =
        innerR + (s.proposalOuterRadiusVb - innerR) * scale;
      const scaledBaselineOuter =
        innerR + (s.baselineOuterRadiusVb - innerR) * scale;
      return {
        ...s,
        proposalOuterRadiusVb: scaledProposalOuter,
        baselineOuterRadiusVb: scaledBaselineOuter,
        leaderOuterRadiusVb: Math.max(scaledBaselineOuter, scaledProposalOuter),
      };
    });
  }
  return rawSlices;
}

/**
 * Path-degree range of a slice's flat outer arc — the outer edge with the
 * rounded corners excluded. Leader lines anchor inside this range so they never
 * attach where the corner radius has begun.
 */
export function getProductGroupSliceFlatOuterArcRangeDeg(
  geometry: Pick<
    ProductGroupDonutSliceGeometry,
    'startAngle' | 'endAngle' | 'proposalOuterRadiusVb'
  >,
  innerRadius = PRODUCT_GROUPS_DONUT_INNER_RADIUS_VB,
): { startDeg: number; endDeg: number } {
  const { startAngle, endAngle, proposalOuterRadiusVb } = geometry;
  const sweep = endAngle - startAngle;
  const cornerRadius = getProductGroupDonutSliceCornerRadiusVb(
    proposalOuterRadiusVb,
    innerRadius,
    startAngle,
    endAngle,
  );
  const outerDelta = Math.min(
    (cornerRadius / proposalOuterRadiusVb) * (180 / Math.PI),
    sweep / 2,
  );
  return { startDeg: startAngle + outerDelta, endDeg: endAngle - outerDelta };
}

/**
 * Anchor on the donut outer edge after the chart's −90° SVG rotation
 * (viewBox coordinates).
 */
export function getDonutSliceAnchorViewBox(
  midAnglePath: number,
  outerRadiusVb: number,
  cx = 50,
  cy = 50,
): { x: number; y: number } {
  const { x: x0, y: y0 } = polarToCartesian(
    cx,
    cy,
    midAnglePath,
    outerRadiusVb,
  );
  return { x: cx + (y0 - cy), y: cy - (x0 - cx) };
}

/** Map a donut viewBox point to page-relative CSS `%` for overlay layout. */
export function donutViewBoxPointToPagePercent(
  xVb: number,
  yVb: number,
): { leftPct: number; topPct: number } {
  const donutLeft =
    PRODUCT_GROUPS_DONUT_CENTER_X_PERCENT -
    PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT / 2;
  const donutTop =
    PRODUCT_GROUPS_DONUT_CENTER_Y_PERCENT -
    PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT / 2;

  return {
    leftPct:
      donutLeft + (xVb / 100) * PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT,
    topPct: donutTop + (yVb / 100) * PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT,
  };
}
